import React, { useEffect } from 'react';
import {
  ArrowRight,
  Bell,
  BookOpen,
  CheckCircle2,
  Database,
  FileSearch,
  Gift,
  Globe,
  HeartPulse,
  Search,
  ShieldCheck,
  Sparkles
} from 'lucide-react';

const SITE_URL = 'https://fiscal.mateusemth.dev';

const landingPages = {
  '/monitor-fiscal-gratis': {
    slug: 'monitor-fiscal-gratis',
    eyebrow: 'Monitor fiscal gratuito',
    title: 'Monitor Fiscal grátis para acompanhar SEFAZ, SVRS e documentos fiscais',
    description: 'Use o Monitor Fiscal gratuito para consultar documentos técnicos, notas fiscais eletrônicas, status SEFAZ/SVRS, contingências e alertas de serviços fiscais em um só lugar.',
    heroText: 'Uma ferramenta gratuita para reduzir consulta manual em portais fiscais. Pesquise documentos técnicos, acompanhe disponibilidade dos serviços e encontre sinais importantes sobre DF-e, Nota Fiscal eletrônica e publicações SEFAZ.',
    primaryKeyword: 'monitor fiscal grátis',
    relatedTerms: ['monitor fiscal gratuito', 'monitor fiscal de graça', 'documento fiscal', 'SEFAZ', 'DF-e', 'Nota Fiscal'],
    sections: [
      {
        title: 'O que o Monitor Fiscal gratuito resolve',
        body: 'Quem trabalha com documento fiscal eletrônico precisa conferir portais, status de autorização, notas técnicas, schemas XML e comunicados operacionais. O Monitor Fiscal organiza esses pontos para que a equipe consulte mais rápido e com menos troca de abas.',
        bullets: [
          'Consulta de publicações técnicas de NF-e, NFC-e, CT-e, MDF-e e outros DF-e.',
          'Leitura de status fiscal, manutenção e contingência quando disponível.',
          'Acompanhamento gratuito para operação fiscal, suporte, produto e tecnologia.'
        ]
      },
      {
        title: 'Por que usar uma consulta fiscal centralizada',
        body: 'A busca manual nos portais oficiais continua importante, mas é lenta para rotina diária. Uma camada de monitoramento ajuda a descobrir novidades, organizar histórico e acionar alertas quando uma mudança fiscal aparece.',
        bullets: [
          'Menos tempo procurando documentos fiscais em fontes diferentes.',
          'Mais visibilidade sobre instabilidade, parada ou manutenção de serviço fiscal.',
          'Base prática para análise grátis antes de envolver desenvolvimento ou suporte.'
        ]
      }
    ],
    faq: [
      ['O Monitor Fiscal é grátis?', 'Sim. A consulta principal é gratuita e pode ser usada para acompanhar documentos técnicos, status dos serviços e publicações fiscais disponíveis.'],
      ['É um serviço oficial da SEFAZ?', 'Não. É uma ferramenta independente que organiza informações públicas e operacionais relacionadas a SEFAZ, SVRS e documentos fiscais eletrônicos.'],
      ['Quais documentos fiscais posso acompanhar?', 'O foco inclui DF-e como NF-e, NFC-e, CT-e, MDF-e, BP-e, NFCom, NF3-e e outros portais fiscais monitorados.']
    ]
  },
  '/analise-gratis-documento-fiscal': {
    slug: 'analise-gratis-documento-fiscal',
    eyebrow: 'Análise grátis',
    title: 'Análise grátis de documento fiscal, notas técnicas e mudanças em DF-e',
    description: 'Faça análise grátis de documentos fiscais eletrônicos, compare versões de publicações SEFAZ/SVRS e encontre alterações relevantes em notas técnicas, manuais e schemas.',
    heroText: 'Quando uma nota técnica ou manual muda, a dúvida aparece rápido: o que mudou, qual DF-e foi impactado e se a equipe precisa agir. Esta página direciona você para uma análise grátis com consulta, histórico e comparação de versões.',
    primaryKeyword: 'análise grátis documento fiscal',
    relatedTerms: ['análise gratuita', 'análise de graça', 'documento fiscal eletrônico', 'nota técnica SEFAZ', 'schema XML', 'DF-e'],
    sections: [
      {
        title: 'Como funciona a análise grátis',
        body: 'O Monitor Fiscal lista documentos recentes por portal e ajuda a comparar versões relacionadas. Isso facilita identificar alterações em campos, regras, manuais e pacotes técnicos antes de abrir uma demanda interna.',
        bullets: [
          'Selecione o portal fiscal e encontre a publicação desejada.',
          'Consulte histórico e versões quando houver dados monitorados.',
          'Use a comparação para entender mudanças relevantes em documento fiscal.'
        ]
      },
      {
        title: 'Quando vale analisar um documento fiscal',
        body: 'A análise é especialmente útil quando há nova Nota Técnica, pacote de schemas, manual de integração, comunicado de contingência ou mudança operacional em serviço de consulta, autorização ou recepção.',
        bullets: [
          'Antes de atualizar integração fiscal em ERP, SaaS ou gateway de emissão.',
          'Ao investigar rejeições, mudanças de schema XML ou comportamento de autorização.',
          'Durante acompanhamento de NF-e, NFC-e, CT-e, MDF-e e demais DF-e.'
        ]
      }
    ],
    faq: [
      ['A análise grátis substitui parecer fiscal?', 'Não. Ela ajuda a localizar e resumir mudanças técnicas, mas decisões fiscais devem considerar documentos oficiais e orientação especializada.'],
      ['Consigo comparar versões?', 'Sim. Quando há histórico compatível, o comparador mostra versões relacionadas e ajuda a entender diferenças entre documentos.'],
      ['Preciso pagar para começar?', 'Não. Você pode abrir o monitor e iniciar a consulta gratuitamente.']
    ]
  },
  '/consulta-documento-fiscal': {
    slug: 'consulta-documento-fiscal',
    eyebrow: 'Serviço de consulta',
    title: 'Serviço de consulta de documento fiscal eletrônico, SEFAZ e DF-e',
    description: 'Consulte documentos fiscais eletrônicos, publicações técnicas, notas fiscais, manuais, schemas XML e status de serviços SEFAZ/SVRS com o Monitor Fiscal.',
    heroText: 'Um serviço de consulta fiscal precisa ser rápido, claro e útil para rotina. Aqui você encontra documentos técnicos de DF-e, publicações SEFAZ/SVRS, status de portais e caminhos para automação por API.',
    primaryKeyword: 'serviço de consulta documento fiscal',
    relatedTerms: ['consulta documento fiscal', 'consulta nota fiscal', 'consulta DF-e', 'serviço SEFAZ', 'status SEFAZ', 'monitor fiscal'],
    sections: [
      {
        title: 'Consulta fiscal para rotina técnica e operacional',
        body: 'A página inicial do Monitor Fiscal permite escolher o portal, buscar documentos, filtrar publicações e acompanhar sinais de disponibilidade. É uma alternativa prática para conferir novidades sem navegar portal por portal.',
        bullets: [
          'Consulta de documentos técnicos e publicações oficiais monitoradas.',
          'Organização por portal e tipo de documento fiscal eletrônico.',
          'Acesso a status, manutenção e contingência dos serviços acompanhados.'
        ]
      },
      {
        title: 'Integração com API e alertas',
        body: 'Além da interface, o projeto oferece documentação de API, conta de acesso, alertas por e-mail e webhooks. Isso ajuda equipes que precisam levar consulta fiscal para sistemas internos.',
        bullets: [
          'API para consulta de documentos e status fiscal.',
          'Alertas para novas publicações e mudanças importantes.',
          'Webhooks para produtos fiscais, suporte e operação.'
        ]
      }
    ],
    faq: [
      ['O serviço consulta nota fiscal emitida?', 'O foco do Monitor Fiscal é monitorar documentos técnicos, status e publicações fiscais. Ele não é um portal oficial para baixar XML de notas emitidas.'],
      ['Existe API para consulta?', 'Sim. Há documentação interativa e criação de conta para uso da API do Monitor Fiscal.'],
      ['O serviço é gratuito?', 'A consulta principal é gratuita; recursos de API e automação podem exigir configuração de conta.']
    ]
  },
  '/dfe-sefaz': {
    slug: 'dfe-sefaz',
    eyebrow: 'DF-e e SEFAZ',
    title: 'DF-e SEFAZ: monitoramento de NF-e, NFC-e, CT-e, MDF-e e documentos fiscais',
    description: 'Acompanhe DF-e na SEFAZ e SVRS: NF-e, NFC-e, CT-e, MDF-e, BP-e, NFCom, NF3-e, notas técnicas, schemas XML, manuais e status dos serviços.',
    heroText: 'DF-e muda o tempo todo: notas técnicas, schemas, manuais, ambientes de autorização e serviços de consulta. O Monitor Fiscal ajuda a acompanhar esses sinais em uma experiência gratuita e direta.',
    primaryKeyword: 'DF-e SEFAZ',
    relatedTerms: ['NF-e', 'NFC-e', 'CT-e', 'MDF-e', 'Nota Fiscal', 'SEFAZ', 'SVRS', 'documento fiscal eletrônico'],
    sections: [
      {
        title: 'Documentos fiscais eletrônicos monitorados',
        body: 'O Monitor Fiscal acompanha portais relacionados a documentos fiscais eletrônicos e publicações técnicas. Isso inclui famílias como Nota Fiscal eletrônica, Conhecimento de Transporte eletrônico, Manifesto eletrônico e documentos fiscais de comunicação, energia e transporte.',
        bullets: [
          'NF-e e NFC-e para rotinas de emissão e varejo.',
          'CT-e, CT-e OS e MDF-e para transporte e logística.',
          'BP-e, NFCom, NF3-e, NFF, NFAG e outros documentos acompanhados.'
        ]
      },
      {
        title: 'Status SEFAZ e impacto operacional',
        body: 'A instabilidade de um serviço fiscal pode afetar emissão, consulta, autorização, contingência e suporte. Ter uma visão consolidada ajuda a diferenciar falha interna de indisponibilidade externa.',
        bullets: [
          'Acompanhe sinais de serviço indisponível, manutenção e contingência.',
          'Consulte documentos recentes relacionados ao DF-e afetado.',
          'Compartilhe evidências com times de suporte, produto e tecnologia.'
        ]
      }
    ],
    faq: [
      ['O que significa DF-e?', 'DF-e é documento fiscal eletrônico, categoria que inclui NF-e, NFC-e, CT-e, MDF-e e outros documentos digitais usados em rotinas fiscais brasileiras.'],
      ['O Monitor Fiscal cobre todos os estados?', 'O foco é em portais e serviços públicos monitoráveis, especialmente SEFAZ e SVRS. A cobertura pode variar por documento e fonte disponível.'],
      ['Consigo ver status da SEFAZ?', 'Sim. A ferramenta mostra status e sinais de disponibilidade quando a fonte pública permite consulta confiável.']
    ]
  },
  '/nota-fiscal-sefaz': {
    slug: 'nota-fiscal-sefaz',
    eyebrow: 'Nota Fiscal e SEFAZ',
    title: 'Nota Fiscal SEFAZ: acompanhe publicações, status e documentos técnicos',
    description: 'Monitore Nota Fiscal eletrônica, NF-e, NFC-e, publicações SEFAZ, notas técnicas, schemas XML, manuais e status dos serviços fiscais gratuitamente.',
    heroText: 'Para quem integra ou opera Nota Fiscal eletrônica, uma publicação da SEFAZ pode gerar ajuste técnico, atualização de schema, revisão de regra ou mudança no suporte. O Monitor Fiscal deixa esse acompanhamento mais simples.',
    primaryKeyword: 'Nota Fiscal SEFAZ',
    relatedTerms: ['Nota Fiscal eletrônica', 'NF-e', 'NFC-e', 'SEFAZ', 'consulta nota fiscal', 'documento fiscal', 'monitor fiscal grátis'],
    sections: [
      {
        title: 'Acompanhe mudanças em Nota Fiscal eletrônica',
        body: 'Notas técnicas, manuais e pacotes XML podem alterar integrações fiscais. O Monitor Fiscal ajuda a localizar publicações recentes e entender o histórico de documentos relacionados a NF-e e NFC-e.',
        bullets: [
          'Consulta gratuita de publicações sobre Nota Fiscal e documentos relacionados.',
          'Histórico monitorado para facilitar análise de versões.',
          'Links para documentos oficiais quando disponíveis no portal de origem.'
        ]
      },
      {
        title: 'Status e serviço de consulta fiscal',
        body: 'Além de documentos, a disponibilidade dos serviços SEFAZ é parte da operação. Acompanhar status ajuda a explicar falhas de consulta, emissão ou autorização que podem não estar no seu sistema.',
        bullets: [
          'Visão de status fiscal e manutenção dos serviços monitorados.',
          'Alertas e automações para reduzir acompanhamento manual.',
          'API para levar sinais fiscais para produtos e rotinas internas.'
        ]
      }
    ],
    faq: [
      ['A ferramenta emite Nota Fiscal?', 'Não. O Monitor Fiscal acompanha documentos técnicos, publicações e status; ele não emite notas fiscais.'],
      ['É útil para desenvolvedores de NF-e?', 'Sim. Desenvolvedores podem consultar notas técnicas, schemas, manuais e sinais de status dos serviços fiscais monitorados.'],
      ['Posso usar de graça?', 'Sim. A consulta principal do monitor é gratuita.']
    ]
  },
  '/api-gratuita-consulta-fiscal': {
    slug: 'api-gratuita-consulta-fiscal',
    eyebrow: 'API gratuita',
    title: 'API gratuita para consulta fiscal centralizada, documentos fiscais e SEFAZ',
    description: 'Conheça uma API gratuita para consulta centralizada de documentos fiscais, DF-e, publicações SEFAZ/SVRS, status fiscal, notas técnicas e serviços fiscais.',
    heroText: 'Para produtos fiscais, ERPs, suporte e times de tecnologia, consultar cada portal manualmente vira gargalo. A API gratuita do Monitor Fiscal ajuda a centralizar consulta de documentos fiscais, status SEFAZ/SVRS e publicações técnicas em um ponto único.',
    primaryKeyword: 'API gratuita consulta fiscal',
    relatedTerms: ['api gratis consulta fiscal', 'api de graça documento fiscal', 'consulta fiscal centralizada', 'documento fiscal', 'SEFAZ', 'DF-e'],
    sections: [
      {
        title: 'Consulta centralizada por API, grátis para começar',
        body: 'A proposta é simplificar o acesso a informações fiscais públicas monitoradas: documentos técnicos, publicações recentes, status de serviços e sinais de operação. Em vez de depender de conferência manual, sua aplicação pode consultar uma camada organizada.',
        bullets: [
          'API grátis para iniciar consulta centralizada de documentos fiscais.',
          'Endpoints para documentos, status fiscal e notificações monitoradas.',
          'Base útil para SaaS fiscal, ERP, suporte, produto e operação técnica.'
        ]
      },
      {
        title: 'Por que centralizar consulta fiscal',
        body: 'Dados fiscais ficam espalhados entre portais, páginas de status, comunicados e arquivos técnicos. Centralizar a consulta reduz atrito, melhora a rastreabilidade e ajuda sistemas internos a reagirem mais rápido a mudanças em DF-e.',
        bullets: [
          'Menos scraping improvisado em cada produto ou rotina interna.',
          'Consulta padronizada para NF-e, NFC-e, CT-e, MDF-e e outros DF-e.',
          'Caminho simples para alertas, dashboards e automações fiscais.'
        ]
      }
    ],
    faq: [
      ['A API é gratuita?', 'Há uso gratuito para começar a consultar documentos e status monitorados. Recursos e limites podem depender da configuração da conta.'],
      ['A API consulta documentos fiscais emitidos?', 'O foco é consulta de publicações técnicas, documentos fiscais normativos/operacionais e status de serviços, não o download oficial de XML emitido.'],
      ['Preciso integrar tudo do zero?', 'Não. A documentação da API ajuda a testar endpoints e entender os retornos antes de automatizar em produto ou rotina interna.']
    ]
  },
  '/centralizar-dados-fiscais': {
    slug: 'centralizar-dados-fiscais',
    eyebrow: 'Dados fiscais centralizados',
    title: 'Centralize dados fiscais e monitore SEFAZ, SVRS e documentos fiscais',
    description: 'Centralize dados fiscais, documentos técnicos, publicações SEFAZ/SVRS, status de serviços, contingências e mudanças em DF-e com o Monitor Fiscal.',
    heroText: 'A rotina fiscal digital envolve portais, notas técnicas, manuais, schemas, status de autorização e comunicados. Centralizar esses dados ajuda equipes a enxergar o que mudou, onde houve impacto e qual documento fiscal precisa de atenção.',
    primaryKeyword: 'centralizar dados fiscais',
    relatedTerms: ['dados fiscais centralizados', 'monitorar SEFAZ', 'documentos fiscais', 'status fiscal', 'SVRS', 'Nota Fiscal'],
    sections: [
      {
        title: 'Uma visão única para a operação fiscal',
        body: 'Centralizar dados fiscais não significa substituir fontes oficiais. Significa organizar sinais importantes em uma leitura prática: documentos novos, alterações técnicas, status dos serviços e histórico disponível para acompanhamento.',
        bullets: [
          'Reúna publicações fiscais relevantes em uma experiência de consulta.',
          'Acompanhe status SEFAZ/SVRS sem abrir várias páginas separadas.',
          'Facilite análise de impacto para operação, suporte e tecnologia.'
        ]
      },
      {
        title: 'Monitoramento de SEFAZ para agir mais rápido',
        body: 'Quando um serviço fiscal oscila ou uma publicação técnica aparece, a equipe precisa diferenciar ruído de impacto real. O Monitor Fiscal ajuda a criar esse ponto de observação contínuo para dados fiscais públicos.',
        bullets: [
          'Identifique sinais de manutenção, indisponibilidade e contingência.',
          'Encontre documentos técnicos relacionados a cada portal fiscal.',
          'Reduza dependência de acompanhamento manual e mensagens dispersas.'
        ]
      }
    ],
    faq: [
      ['A API avisa quando a SEFAZ cai?', 'Sim. Através dos endpoints de monitoramento e webhooks, você consegue identificar quedas, manutenções programadas e entradas em contingência (SVC).'],
      ['Isso substitui o ERP?', 'De forma alguma. O Monitor Fiscal é uma ferramenta de apoio para que o seu ERP ou emissor não fique as cegas e tome decisões inteligentes antes de enviar o lote.'],
      ['Como inicio a integração?', 'Acesse a aba API, veja nossos exemplos no GitHub (Node, Python, C#, etc.) e gere seu token gratuito.']
    ]
  },
  '/exemplos-integracao-api-sefaz': {
    slug: 'exemplos-integracao-api-sefaz',
    eyebrow: 'Integração para Devs',
    title: 'Exemplos de Integração API Sefaz - Monitor Fiscal',
    description: 'Coleção oficial de exemplos de integração da API do Monitor Fiscal em diversas linguagens (Node.js, Python, C#, PHP, Delphi, Go, C++). Automatize a inteligência tributária do seu ERP.',
    heroText: 'Se o seu ERP (sistema de gestão) sofre com instabilidades na autorização de NF-e, se o seu time de suporte perde horas tentando descobrir se a Sefaz está fora do ar, ou se você é sempre pego de surpresa quando o fisco publica um novo Schema XML, esta API foi feita para você.',
    primaryKeyword: 'exemplos integração api sefaz',
    githubLink: 'https://github.com/mateusemth/api-monitor-sefaz',
    relatedTerms: ['integração api monitor fiscal', 'api sefaz nodejs', 'api sefaz python', 'api sefaz delphi', 'automação notas técnicas'],
    sections: [
      {
        title: 'O que a API resolve na prática?',
        body: 'Ao incorporar a API do Monitor Fiscal no seu sistema, você passa a ter superpoderes fiscais de forma programática. Tudo o que você fazia manualmente, como ler portais do governo e monitorar a disponibilidade, agora é automatizado via JSON.',
        bullets: [
          'Auto-Contingência: Cheque a disponibilidade dos portais (ex: SVRS, Sefaz-SP) e altere a emissão para contingência antes do cliente ligar reclamando.',
          'Webhooks e Notificações: Consuma a fila de alertas para ser avisado sobre instabilidades assim que elas são identificadas.',
          'Download de NTs e Schemas: Extraia links, manuais e atualizações lendo um JSON limpo.',
          'Diffs Fiscais Automáticos: Compare versões antigas com as versões novas das Notas Técnicas publicadas.'
        ]
      },
      {
        title: 'Entendendo os Principais Endpoints',
        body: 'Os scripts de exemplo fazem uma tour completa pelos pilares da documentação OpenAPI. Você aprende consumindo código pronto e não precisando ler documentações extensas e cansativas.',
        bullets: [
          'GET /api/fiscal-status: Retorna o status de contingência e manutenção dos webservices.',
          'GET /api/fiscal-api: Scraper em Tempo Real que aciona os rastreadores do Monitor Fiscal para buscar a versão da fonte no momento da requisição.',
          'GET /api/fiscal-notifications: Consulta a fila de eventos pendentes e alertas de contingência (ideal para cron-jobs).'
        ]
      },
      {
        title: 'Rate Limit e Prevenção de Bloqueios',
        body: 'Para garantir a estabilidade do sistema, a API implementa um controle rigoroso de requisições. O projeto foi criado pela comunidade técnica para a comunidade técnica, oferecendo 120 requisições por minuto (RPM) gratuitamente.',
        bullets: [
          'X-RateLimit-Remaining: Quantas requisições você ainda pode fazer antes de ser bloqueado.',
          '429 Too Many Requests: O sistema bloqueará temporariamente novas chamadas caso o limite seja atingido.',
          'Precisa de mais escala? Negocie um limite corporativo para a sua Software House enviando email para me@mateusemth.dev.'
        ]
      }
    ],
    faq: [
      ['Onde encontro os códigos?', 'Todos os nossos exemplos (Node, Python, C#, Go, C++, Delphi e PHP) estão disponíveis de forma Open Source na aba API do site ou diretamente no nosso repositório no GitHub.'],
      ['A integração é gratuita?', 'Sim. O serviço oferece um limite inicial de 120 requisições por minuto 100% gratuitamente.'],
      ['Como autentico as requisições?', 'A integração exige o envio do cabeçalho "Authorization: Bearer SUA_API_KEY". Crie a sua chave gratuita no Painel de Desenvolvedor.']
    ]
  },
  '/monitoramento-fontes-governamentais': {
    slug: 'monitoramento-fontes-governamentais',
    eyebrow: 'Fontes governamentais',
    title: 'Monitoramento de fontes governamentais para SEFAZ, SVRS e documentos fiscais',
    description: 'Monitore informações fiscais a partir de fontes governamentais públicas: SEFAZ, SVRS, documentos fiscais, notas técnicas, schemas, manuais e status de serviços.',
    heroText: 'O Monitor Fiscal acompanha fontes públicas governamentais e portais fiscais para organizar documentos, status e sinais operacionais. A ferramenta é independente, mas busca manter a consulta próxima das fontes oficiais de SEFAZ, SVRS e documentos fiscais eletrônicos.',
    primaryKeyword: 'monitoramento fontes governamentais fiscais',
    relatedTerms: ['fontes oficiais SEFAZ', 'monitoramento governamental', 'status SEFAZ', 'SVRS', 'documentos fiscais oficiais', 'Nota Fiscal'],
    sections: [
      {
        title: 'Direto de fontes públicas e portais fiscais',
        body: 'A confiança do acompanhamento fiscal depende da origem da informação. Por isso, o monitoramento prioriza páginas públicas, portais governamentais e fontes ligadas a serviços fiscais oficiais quando disponíveis.',
        bullets: [
          'Acompanhamento de portais SEFAZ, SVRS e serviços fiscais públicos.',
          'Consulta de documentos, manuais, notas técnicas e schemas publicados.',
          'Organização das informações sem apresentar a ferramenta como órgão oficial.'
        ]
      },
      {
        title: 'Monitoramento oficial sem confundir a origem',
        body: 'Na prática, muitas buscas usam o termo monitoramento oficial para procurar dados vindos de fontes oficiais. O Monitor Fiscal atende essa intenção ao organizar informações públicas governamentais, mantendo transparência: é uma solução independente.',
        bullets: [
          'Dados rastreados de fontes governamentais quando disponíveis publicamente.',
          'Links e contexto para conferir a origem da publicação fiscal.',
          'Visão prática para diferenciar falha interna de instabilidade externa.'
        ]
      }
    ],
    faq: [
      ['O Monitor Fiscal é oficial do governo?', 'Não. O Monitor Fiscal é independente. Ele organiza informações públicas e sinais obtidos de fontes governamentais e portais fiscais quando disponíveis.'],
      ['O que significa direto de fontes governamentais?', 'Significa priorizar portais e páginas públicas ligadas a SEFAZ, SVRS e documentos fiscais, preservando contexto de origem sempre que possível.'],
      ['Posso usar para acompanhar status oficial da SEFAZ?', 'Você pode usar como camada de monitoramento e consulta. Para decisão final, sempre valide também a fonte oficial correspondente.']
    ]
  },
  '/api-sefaz-desenvolvedores': {
    slug: 'api-sefaz-desenvolvedores',
    eyebrow: 'API para Desenvolvedores',
    title: 'API Gratuita de Monitoramento SEFAZ e DF-e para Desenvolvedores',
    description: 'Integre a API gratuita de monitoramento de disponibilidade da SEFAZ, SVRS, Notas Técnicas e DF-e (NF-e, CT-e, MDF-e) em seu ERP. 120 requisições por minuto.',
    heroText: 'Quem desenvolve sistemas fiscais e ERP sabe a dor de lidar com instabilidades. Use a API do Monitor Fiscal para automatizar a checagem de serviços, verificar contingências e ser notificado sobre novas publicações técnicas em tempo real.',
    primaryKeyword: 'api sefaz desenvolvedores',
    relatedTerms: ['api sefaz gratuita', 'status sefaz api', 'monitoramento df-e', 'api para erp', 'desenvolvedor fiscal', 'software house'],
    sections: [
      {
        title: 'Como a API gratuita ajuda seu ERP ou Software House',
        body: 'A automatização da consulta de disponibilidade da SEFAZ e SVRS evita chamados desnecessários no seu suporte. A API oferece dados em JSON para seu sistema entrar em contingência automaticamente.',
        bullets: [
          'Checagem automatizada de disponibilidade de serviço (Sefaz e SVRS).',
          'Notificações sobre contingência antes que o seu cliente reclame.',
          'Consumo via JSON simples para Node.js, C#, PHP, Delphi, entre outras.'
        ]
      },
      {
        title: 'Acompanhamento de Novas Publicações',
        body: 'Além do status de operação, a API retorna as últimas publicações técnicas de DF-e, como Schemas XML, Manuais e Notas Técnicas.',
        bullets: [
          'Monitore alterações de NF-e, NFC-e, CT-e e MDF-e.',
          'Limite inicial amigável (120 req/min) 100% grátis.',
          'Precisa de mais? Entre em contato para aumentarmos o limite.'
        ]
      }
    ],
    faq: [
      ['A API é realmente gratuita?', 'Sim. O serviço oferece um limite inicial de 120 requisições por minuto gratuitamente. Caso seu ERP precise de um volume maior, basta entrar em contato em me@mateusemth.dev.'],
      ['Preciso criar uma conta para usar?', 'Você pode testar rapidamente sem autenticação, e gerar sua chave (API Key) gratuitamente na nossa página para limites maiores.'],
      ['Quais DF-e são cobertos pela API?', 'O monitoramento abrange NF-e, CT-e, MDF-e, entre outros DF-e autorizados pelas principais Secretarias da Fazenda e SVRS.']
    ],
    codeSnippets: [
      {
        language: 'JavaScript (Node/Browser)',
        code: `fetch("https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5")\n  .then(res => res.json())\n  .then(data => console.log(data));`
      },
      {
        language: 'Python',
        code: `import requests\n\nres = requests.get("https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5")\nprint(res.json())`
      },
      {
        language: 'C# (.NET)',
        code: `var client = new HttpClient();\nvar json = await client.GetStringAsync(\n  "https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5"\n);\nConsole.WriteLine(json);`
      },
      {
        language: 'Delphi / Pascal',
        code: `RESTClient1.BaseURL := 'https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5';\nRESTRequest1.Execute;\nShowMessage(RESTResponse1.Content);`
      },
      {
        language: 'Go',
        code: `resp, _ := http.Get("https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5")\nbody, _ := io.ReadAll(resp.Body)\nfmt.Println(string(body))`
      },
      {
        language: 'C++',
        code: `// Utilizando libcurl / cpr\ncpr::Response r = cpr::Get(\n  cpr::Url{"https://fiscal.mateusemth.dev/api/fiscal-documents?portal=nfe&limit=5"}\n);\nstd::cout << r.text << std::endl;`
      }
    ]
  }
};

export const SEO_LANDING_ROUTES = new Set(Object.keys(landingPages));
export const LP_INDEX_ROUTES = new Set(['/lp', '/lp/']);
export const SEO_LANDING_PAGE_ITEMS = Object.entries(landingPages).map(([path, page]) => ({
  path,
  slug: page.slug,
  title: page.title,
  description: page.description,
  primaryKeyword: page.primaryKeyword,
  relatedTerms: page.relatedTerms
}));

function updateMeta(selector, attribute, value) {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
}

function setStructuredData(page) {
  const existing = document.getElementById('seo-landing-structured-data');
  existing?.remove();

  const script = document.createElement('script');
  script.id = 'seo-landing-structured-data';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: page.title,
        description: page.description,
        url: `${SITE_URL}/${page.slug}`,
        inLanguage: 'pt-BR',
        isPartOf: {
          '@type': 'WebSite',
          name: 'Monitor Fiscal',
          url: `${SITE_URL}/`
        },
        about: page.relatedTerms.map((name) => ({ '@type': 'Thing', name }))
      },
      {
        '@type': 'FAQPage',
        mainEntity: page.faq.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer
          }
        }))
      }
    ]
  });
  document.head.appendChild(script);
}

function LandingHeader() {
  return (
    <header className="border-b border-gray-200 bg-white/95 backdrop-blur dark:border-gray-800 dark:bg-gray-950/95">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-6">
        <a href="/" className="flex min-w-0 items-center gap-3">
          <img src="/icon-master.png" alt="" aria-hidden="true" className="h-10 w-10 shrink-0 rounded-xl object-contain" />
          <div className="min-w-0">
            <p className="truncate text-sm font-black uppercase text-gray-950 dark:text-white">Monitor Fiscal</p>
            <p className="truncate text-xs font-bold text-gray-500 dark:text-gray-400">Documentos fiscais, SEFAZ e DF-e</p>
          </div>
        </a>

        <nav className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
          <a href="/monitor-fiscal-gratis" className="px-2 py-2 hover:text-teal-700 dark:hover:text-teal-300">Grátis</a>
          <a href="/analise-gratis-documento-fiscal" className="px-2 py-2 hover:text-teal-700 dark:hover:text-teal-300">Análise</a>
          <a href="/consulta-documento-fiscal" className="px-2 py-2 hover:text-teal-700 dark:hover:text-teal-300">Consulta</a>
          <a href="/dfe-sefaz" className="px-2 py-2 hover:text-teal-700 dark:hover:text-teal-300">DF-e</a>
          <a href="/api-sefaz-desenvolvedores" className="px-2 py-2 hover:text-teal-700 dark:hover:text-teal-300">API</a>
        </nav>
      </div>
    </header>
  );
}

export function LandingPagesIndex() {
  useEffect(() => {
    document.title = 'Landing Pages SEO - Monitor Fiscal';
    updateMeta('meta[name="description"]', 'content', 'Lista interna de landing pages SEO do Monitor Fiscal para revisão.');
    updateMeta('link[rel="canonical"]', 'href', `${SITE_URL}/lp`);
    updateMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    updateMeta('meta[property="og:title"]', 'content', 'Landing Pages SEO - Monitor Fiscal');
    updateMeta('meta[property="og:description"]', 'content', 'Lista interna de landing pages SEO do Monitor Fiscal para revisão.');
    updateMeta('meta[property="og:url"]', 'content', `${SITE_URL}/lp`);
    updateMeta('meta[name="twitter:title"]', 'content', 'Landing Pages SEO - Monitor Fiscal');
    updateMeta('meta[name="twitter:description"]', 'content', 'Lista interna de landing pages SEO do Monitor Fiscal para revisão.');

    return () => {
      updateMeta('meta[name="robots"]', 'content', 'index, follow');
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <main className="mx-auto max-w-6xl">
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
          <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">Página interna</p>
          <h1 className="mt-2 text-3xl font-black text-gray-950 dark:text-white">Landing pages SEO</h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-gray-600 dark:text-gray-300">
            Lista oculta para revisão das páginas de marketing. Esta rota não entra no sitemap e usa noindex.
          </p>
        </div>

        <section className="mt-5 grid gap-4 md:grid-cols-2">
          {SEO_LANDING_PAGE_ITEMS.map((page) => (
            <article key={page.path} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">{page.primaryKeyword}</p>
              <h2 className="mt-2 text-lg font-black text-gray-950 dark:text-white">{page.title}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{page.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {page.relatedTerms.slice(0, 6).map((term) => (
                  <span key={term} className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300">
                    {term}
                  </span>
                ))}
              </div>
              <a
                href={page.path}
                className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
              >
                Abrir landing
                <ArrowRight className="h-4 w-4" />
              </a>
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}

function CtaButton({ href, children, variant = 'primary' }) {
  const classes = variant === 'primary'
    ? 'bg-teal-600 text-white hover:bg-teal-700'
    : 'border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800';

  return (
    <a href={href} className={`inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-[11px] font-black uppercase tracking-widest transition-colors ${classes}`}>
      {children}
    </a>
  );
}

function SeoLandingPage() {
  const currentPath = window.location.pathname;
  const page = landingPages[currentPath] || landingPages['/monitor-fiscal-gratis'];
  const canonicalUrl = `${SITE_URL}/${page.slug}`;

  useEffect(() => {
    document.title = page.title;
    updateMeta('meta[name="description"]', 'content', page.description);
    updateMeta('link[rel="canonical"]', 'href', canonicalUrl);
    updateMeta('meta[property="og:title"]', 'content', page.title);
    updateMeta('meta[property="og:description"]', 'content', page.description);
    updateMeta('meta[property="og:url"]', 'content', canonicalUrl);
    updateMeta('meta[name="twitter:title"]', 'content', page.title);
    updateMeta('meta[name="twitter:description"]', 'content', page.description);
    setStructuredData(page);

    return () => {
      document.getElementById('seo-landing-structured-data')?.remove();
    };
  }, [canonicalUrl, page]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <LandingHeader />

      <main>
        <section className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:px-6 lg:py-14">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-teal-700 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-300">
                <Gift className="h-3.5 w-3.5" />
                {page.eyebrow}
              </div>
              <h1 className="mt-5 max-w-4xl text-3xl font-black leading-tight text-gray-950 dark:text-white sm:text-5xl">
                {page.title}
              </h1>
              <p className="mt-5 max-w-3xl text-base leading-8 text-gray-600 dark:text-gray-300">
                {page.heroText}
              </p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <CtaButton href="/">
                  Abrir monitor grátis
                  <ArrowRight className="h-4 w-4" />
                </CtaButton>
                {page.githubLink ? (
                  <CtaButton href={page.githubLink} variant="secondary">
                    Ver no GitHub
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                  </CtaButton>
                ) : (
                  <CtaButton href="/api-docs" variant="secondary">
                    Ver API
                    <BookOpen className="h-4 w-4" />
                  </CtaButton>
                )}
              </div>
            </div>

            <aside className="self-start rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center gap-3">
                <img src="/mascot-ops.png" alt="Monitor Fiscal em operação" className="h-16 w-16 rounded-lg object-contain" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">Busca principal</p>
                  <p className="mt-1 text-sm font-black text-gray-950 dark:text-white">{page.primaryKeyword}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {page.relatedTerms.map((term) => (
                  <span key={term} className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-bold text-gray-600 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-300">
                    {term}
                  </span>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-4 px-4 py-8 lg:grid-cols-3 lg:px-6">
          {[
            ['Consulta gratuita', 'Pesquise documentos fiscais, publicações e sinais de status sem custo inicial.', Search],
            ['SEFAZ e SVRS', 'Acompanhe portais fiscais e serviços relacionados a DF-e, Nota Fiscal e documentos técnicos.', Globe],
            ['Alertas e API', 'Leve monitoramento fiscal para e-mail, webhooks e sistemas internos quando precisar automatizar.', Bell]
          ].map(([title, body, Icon]) => (
            <article key={title} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
              <Icon className="h-6 w-6 text-teal-600 dark:text-teal-300" />
              <h2 className="mt-4 text-base font-black text-gray-950 dark:text-white">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{body}</p>
            </article>
          ))}
        </section>

        <section className="mx-auto grid max-w-6xl gap-4 px-4 pb-8 lg:px-6">
          {page.sections.map((section) => (
            <article key={section.title} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">Guia fiscal</p>
                  <h2 className="mt-2 text-2xl font-black text-gray-950 dark:text-white">{section.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-gray-600 dark:text-gray-300">{section.body}</p>
                </div>
                <div className="grid gap-3">
                  {section.bullets.map((item) => (
                    <div key={item} className="flex gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-600 dark:text-teal-300" />
                      <p className="text-sm leading-6 text-gray-700 dark:text-gray-200">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </section>

        {page.codeSnippets && (
          <section className="mx-auto max-w-6xl px-4 pb-8 lg:px-6">
            <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
              <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">Integração Prática</p>
              <h2 className="mt-2 mb-6 text-2xl font-black text-gray-950 dark:text-white">Exemplos de Código</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {page.codeSnippets.map((snippet) => (
                  <div key={snippet.language} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-950">
                    <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      {snippet.language}
                    </div>
                    <pre className="p-4 text-[11px] sm:text-xs overflow-x-auto text-gray-800 dark:text-gray-300 font-mono leading-relaxed">
                      <code>{snippet.code}</code>
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="border-y border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
          <div className="mx-auto grid max-w-6xl gap-4 px-4 py-8 md:grid-cols-4 lg:px-6">
            {[
              ['DF-e', 'NF-e, NFC-e, CT-e, MDF-e e outros documentos fiscais eletrônicos.', FileSearch],
              ['Status fiscal', 'Disponibilidade, manutenção e contingência dos serviços acompanhados.', HeartPulse],
              ['Histórico', 'Base de documentos monitorados para consulta e comparação.', Database],
              ['Independente', 'Ferramenta não oficial, criada para organizar informação pública.', ShieldCheck]
            ].map(([title, body, Icon]) => (
              <article key={title} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
                <Icon className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                <h2 className="mt-3 text-sm font-black text-gray-950 dark:text-white">{title}</h2>
                <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-8 lg:px-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
            <p className="text-[10px] font-black uppercase tracking-widest text-teal-700 dark:text-teal-300">Perguntas frequentes</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950 dark:text-white">Dúvidas sobre {page.primaryKeyword}</h2>
            <div className="mt-6 grid gap-3 md:grid-cols-3">
              {page.faq.map(([question, answer]) => (
                <article key={question} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
                  <h3 className="text-sm font-black text-gray-950 dark:text-white">{question}</h3>
                  <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">{answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 pb-10 lg:px-6">
          <div className="rounded-lg bg-gray-950 p-6 text-white dark:bg-white dark:text-gray-950">
            <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-teal-300 dark:text-teal-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Grátis, gratuito e direto ao ponto
                </div>
                <h2 className="mt-3 text-2xl font-black">Comece pelo monitor e encontre o documento fiscal que você precisa.</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-300 dark:text-gray-700">
                  Abra a consulta, selecione o portal fiscal e acompanhe publicações, status SEFAZ/SVRS, DF-e e Nota Fiscal com menos trabalho manual.
                </p>
              </div>
              <CtaButton href="/">
                Usar de graça
                <ArrowRight className="h-4 w-4" />
              </CtaButton>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SeoLandingPage;
